// AMC status context — single source of truth for "is the Audiences feature on?"
//
// One fetch of /api/amc/status on mount, shared app-wide. Drives (a) the
// Sidebar "Audiences" tab visibility and (b) the /audiences route gate. The
// default is DISABLED so that, with no provider or while loading, the tab stays
// hidden and the route redirects — matching the dark-launch contract (with
// CLICKPAY_AMC off the backend returns enabled:false anyway).
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, type AmcStatus } from './api'

const DISABLED: AmcStatus = {
  enabled: false, configured: false, instance_provisioned: false,
  // Default to the honest dark state so consumers (the activation wizard's
  // banner) can read amc_activation_status without a null-guard.
  amc_activation_status: { activation_enabled: false, live_writes_enabled: false },
}

type AmcStatusCtx = { status: AmcStatus; loading: boolean }

const AmcStatusContext = createContext<AmcStatusCtx>({ status: DISABLED, loading: false })

export function AmcStatusProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AmcStatus>(DISABLED)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    api.getAmcStatus()
      .then(s => { if (!cancelled) setStatus(s) })
      .catch(() => { /* network/4xx → stay disabled (safe default) */ })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  return (
    <AmcStatusContext.Provider value={{ status, loading }}>
      {children}
    </AmcStatusContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- context hook lives with its provider by design (same pattern as auth.tsx's useAuth)
export function useAmcStatus(): AmcStatusCtx {
  return useContext(AmcStatusContext)
}

/** Convenience: is the Audiences feature enabled? False while loading / no provider. */
// eslint-disable-next-line react-refresh/only-export-components -- context hook lives with its provider by design (same pattern as auth.tsx's useAuth)
export function useAmcEnabled(): boolean {
  return useContext(AmcStatusContext).status.enabled
}
